import interact from 'interactjs';
import { COLOR_VAR_TO_HEX_MAPPING, REGION_SNAP_GUTTER, REGION_SNAP_RANGE } from 'lib/constants.js';
import { expandRect, edgeIsNear, verticalIntersection, horizontalIntersection } from 'lib/rect';
import { nui } from 'lib/nodes-ui';

class RegionUI {

  constructor() {
    this.selectedRegionIds = new Set();
    this.resetDragData();
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  resetDragData() {
    this.prevRegionPositions = {};
    this.draggingNodes = [];
    this.prevNodePositions = [];
    this.prevLinkAngles = [];
    this.targetNodeIds = [];
    this.targetLinkIds = [];
    this.draggingRegions = [];
  }

  createRegion({ title, color, top, left, right, bottom, operationMessages }) {
    const region = canvas.store.addRegion({
      title: title || 'Untitled Region',
      color: color || COLOR_VAR_TO_HEX_MAPPING.blue,
      top,
      left,
      right,
      bottom,
    });

    if (operationMessages) {
      /* create region as part of some greater batch process, like copy/paste */
      const message = canvas.operation.messages.addRegion({
        regionUUID: region.id,
        title: region.title,
        color: region.color,
        top: region.top,
        left: region.left,
        bottom: region.bottom,
        right: region.right,
        onSuccess: console.log,
        onFailure: console.log,
      });
      operationMessages.push(message);
    } else {
      canvas.operation.addRegion({
        regionUUID: region.id,
        title: region.title,
        color: region.color,
        top: region.top,
        left: region.left,
        bottom: region.bottom,
        right: region.right,
        onSuccess: console.log,
        onFailure: console.log,
      });
      canvas.regionsTag.update();
      canvas.regionTags[region.id].setSelected();
    }

    canvas.appFooterTag.update();
    return region;
  }

  duplicateRegion({ id, x, y }) {
    const region = this.canvas.store.getRegion(id);
    const width = region.right - region.left;
    const height = region.bottom - region.top;
    return this.createRegion({
      title: region.title,
      color: region.color,
      top: y,
      left: x,
      right: x + width,
      bottom: y + height,
    });
  }

  deleteRegion({ id }) {
    const region = this.canvas.store.getRegion(id);
    this.canvas.operation.deleteRegion(Object.assign({
      regionUUID: id,
      onSuccess: console.log,
      onFailure: console.warn,
    }, region));

    this.canvas.store.deleteRegion({ id });
    this.canvas.regionsTag.update();
    delete this.canvas.regionTags[id];
    this.canvas.appFooterTag.update();
    this.removeRegionFromSelection(id);
    this.canvas.multiSelectionIndicatorBox.update();
  }

  getSelectedRegionIds() {
    return this.selectedRegionIds;
  }

  getSelectedRegions() {
    return this.getSelectedRegionIdsArray()
      .map(id => this.canvas.store.getRegion(id));
  }

  getSelectedRegionIdsArray() {
    return Array.from(this.selectedRegionIds);
  }

  deleteSelectedRegions({operationMessages}) {
    if (!this.selectedRegionIds || !this.selectedRegionIds.size) return;
    this.deleteRegions({ids: this.selectedRegionIds, operationMessages});
  }

  deleteRegions({ids,operationMessages}) {
    const messages = [];
    for (const id of ids) {
      const region = this.canvas.store.getRegion(id);
      const message = this.canvas.operation.messages.deleteRegion(Object.assign({ regionUUID: id }, region));
      messages.push(message);
      this.canvas.store.deleteRegion({ id });
      this.removeRegionFromSelection(id);
      delete this.canvas.regionTags[id];
    }

    this.canvas.regionsTag.update();

    if (operationMessages) {
      for (const message of messages) {
        operationMessages.push(message);
      }
    } else {
      this.canvas.operation.batchOperation({
        messages,
        onFailure: console.warn,
      });
    }
    this.canvas.appFooterTag.update();
    this.canvas.multiSelectionIndicatorBox.update();
  }

  selectRegion(regionId,shiftKey) {
    if (!shiftKey) {
      this.clearSelectedRegions();
    }
    this.addRegionToSelection(regionId);
  }

  selectRegions(regionIds) {
    for (const regionId of regionIds) {
      this.addRegionToSelection(regionId);
    }
  }

  getRegionTag(id) {
    return this.canvas.regionTags[id];
  }

  addRegionToSelection(regionId) {
    const { selectedRegionIds } = this;
    const regionTag = this.getRegionTag(regionId);

    if (!selectedRegionIds.has(regionId)) {
      selectedRegionIds.add(regionId);
    }

    if (regionTag) {
      regionTag.setSelected();
    }
  }

  removeRegionFromSelection(regionId) {
    const { selectedRegionIds } = this;

    if (selectedRegionIds.has(regionId)) {
      selectedRegionIds.delete(regionId);

      if (this.draggingRegions) {
        this.draggingRegions = this.draggingRegions.filter( r => r.id !== regionId );
      }

      const regionTag = this.getRegionTag(regionId);

      if (regionTag) {
        regionTag.deSelect();
      }
    }
  }

  clearSelectedRegions() {
    const oldSelection = this.selectedRegionIds;

    if (this.selectedRegionIds.size) {

      for (const id of oldSelection) {
        const regionTag = this.getRegionTag(id);
        if (regionTag) {
          regionTag.deSelect();
        }
      }

      this.resetDragData();
      this.selectedRegionIds.clear();
    }

    this.canvas.bulkActionsMenuTag.update();
    this.canvas.multiSelectionIndicatorBox.update();

    return oldSelection;
  }

  setRegionColor({ ids, color }) {
    const messages = [];
    for (const id of ids) {
      const region = this.canvas.store.getRegion(id);
      if (!region) continue;

      const prevColor = region.color;
      region.setColor(color);
      if (this.canvas.regionTags[id]) {
        this.canvas.regionTags[id].refreshColor();
      }

      const message = this.canvas.operation.messages.updateRegion({
        regionUUID: id,
        prevValues: { color: prevColor },
        updates: { color },
      });
      messages.push(message);
    }

    this.canvas.operation.batchOperation({
      messages,
      onSuccess: console.log,
      onFailure: console.warn,
    });
  }

  getBulkRegionColor(ids) {
    const colors = [];
    const seen = {};
    for (const id of ids) {
      const region = this.canvas.store.getRegion(id);
      if (region && !seen[region.color]) {
        colors.push(region.color);
        seen[region.color] = true;
      }
    }
    if (colors.length == 1) {
      return colors[0];
    }
    return null;
  }

  setupDragging() {
    /* can move and resize; can't create a selection box over the region */
    if (this.dragger) this.dragger.off();
    this.dragger = interact('.region')
      .styleCursor(false)
      .actionChecker((pointer, event, action, interactable, element, interaction) => {
        // if dragging isn't enabled, then the user can use the selection area on a region
        if (action.name == 'drag' && element.getAttribute('data-move-enabled') != 'true') {
          action.name = null;
        }

        return action;
      })
      .draggable({
        max: 3,
        maxPerElement: 3,
        onstart: this.onMoveStart.bind(this),
        onmove: this.onMove.bind(this),
        onend: this.onMoveEnd.bind(this),
      })
      .resizable({
        edges: {
          top: '.region-resize-handle--top',
          left: '.region-resize-handle--left',
          bottom: '.region-resize-handle--bottom',
          right: '.region-resize-handle--right',
        },
        restrictSize: {
          min: {
            height: 150,
            width: 150,
          },
        },
        onstart: this.onResizeStart.bind(this),
        onmove: this.onResize.bind(this),
        onend: this.onResizeEnd.bind(this),
      });
  };

  onResizeMoveStart(e) {
    const regionID = e.target.getAttribute('data-region-id');
    const dragRegion = canvas.regionTags[regionID];
    this.dragRegion = dragRegion;
    this.dragRegion.onResizeMoveStart(e);
  };

  onMoveStart(e) {
    if (!e.interaction.downEvent.target.classList.contains('drag-handle')) {
      return;
    }
    const regionID = e.target.getAttribute('data-region-id');
    const dragRegion = canvas.regionTags[regionID];
    const { isMac } = canvas;
    this.dragRegion = dragRegion;

    if ((isMac && e.metaKey) || (!isMac && e.ctrlKey)) {
      if (!canvas.inTheMiddleOfMetaDrag) {
        canvas.inTheMiddleOfMetaDrag = true;
        dragRegion.toggleSelected(e);
      }
    }

    this.dragRegion.onMoveStart(e);
  }

  onMove(e) {
    e.stopImmediatePropagation();
    if (!e.interaction.downEvent.target.classList.contains('drag-handle')) {
      return;
    }
    this.dragRegion.onMove(e);
  }

  onMoveEnd(e) {
    if (!this.dragRegion) return;
    this.dragRegion.onMoveEnd(e);
    this.dragRegion = null;
  }

  onResizeStart(e) {
    const regionID = e.target.getAttribute('data-region-id');
    const dragRegion = canvas.regionTags[regionID];

    const { downEvent } = e.interaction;
    this.moving = {
      top: downEvent.target.className.includes('top'),
      left: downEvent.target.className.includes('left'),
      bottom: downEvent.target.className.includes('bottom'),
      right: downEvent.target.className.includes('right'),
    };


    this.dragRegion = dragRegion;
    this.dragRegion.onResizeMoveStart(e);
  }

  onResize(e) {
    e.stopImmediatePropagation();
    this.dragRegion.onResize(e);
  }

  onResizeEnd(e) {
    this.dragRegion.onResizeMoveEnd(e);
    this.dragRegion = null;
    this.moving = {};
  }

  getSnappableEdges(unSnappedRect, regionId) {

    const draggingRect = expandRect(unSnappedRect, REGION_SNAP_GUTTER / 2);

    const width = draggingRect.right - draggingRect.left;
    const height = draggingRect.bottom - draggingRect.top;

    const nonSelfRegions = canvas.store.getRegions()
      .filter( region => region.id !== regionId )
      .filter( region => !this.draggingRegions.includes(region) )
      .map(r => expandRect(r, REGION_SNAP_GUTTER / 2));

    const horizontalSnappableRegions = nonSelfRegions
      .filter( region => {
        return (
              Math.abs(region.right - draggingRect.left) < width
              || Math.abs(region.left - draggingRect.right) < width
            )
          && (
            verticalIntersection(draggingRect, region) || verticalIntersection(region, draggingRect)
          );
      });

    const verticalSnappableRegions = nonSelfRegions
      .filter( region => {
        return (
              Math.abs(region.bottom - draggingRect.top) < height
              || Math.abs(region.top - draggingRect.bottom) < height
            )
          && (
            horizontalIntersection(draggingRect, region) || horizontalIntersection(region, draggingRect)
          );
      });

    const edgeMap = {
      'top': 'bottom',
      'bottom': 'top',
      'left': 'right',
      'right': 'left',
    };

    const intersectionFunctionMap = {
      'top': horizontalIntersection,
      'bottom': horizontalIntersection,
      'left': verticalIntersection,
      'right': verticalIntersection,
    };

    let verticalSnapEdge = null;
    let horizontalSnapEdge = null;

    for (const edge of ['top', 'left', 'bottom', 'right']) {
      const oppositeEdge = edgeMap[edge];

      const snappableRegions = nonSelfRegions
        .filter( region => {
          return ( Math.abs(region[oppositeEdge] - draggingRect[edge]) < width)
            && (
              intersectionFunctionMap[edge](draggingRect, region) || intersectionFunctionMap[edge](region, draggingRect)
            );
        });

      for (const region of snappableRegions) {
        if (edgeIsNear(draggingRect[edge], region[oppositeEdge])) {

          if (edge === 'left' || edge === 'right') {

            if (this.moving[edge]) {
              horizontalSnapEdge = {
                ourEdge: edge,
                theirEdge: oppositeEdge,
                region,
              };
            }

            // check to see if we should match our bottom or top with theirs
            const cornerEdge = this.moving.top ? 'top' : this.moving.bottom ? 'bottom' : null;

            if (cornerEdge) {
              if (edgeIsNear(draggingRect[cornerEdge], region[cornerEdge])) {
                verticalSnapEdge = {
                  ourEdge: cornerEdge,
                  theirEdge: cornerEdge,
                  region,
                };
              }
            }
          } else {

            if (this.moving[edge]) {
              verticalSnapEdge = {
                ourEdge: edge,
                theirEdge: oppositeEdge,
                region,
              };
            }

            // check to see if we should match our bottom or top with theirs
            const cornerEdge = this.moving.left ? 'left' : this.moving.right ? 'right' : null;

            if (cornerEdge) {
              if (edgeIsNear(draggingRect[cornerEdge], region[cornerEdge])) {
                horizontalSnapEdge = {
                  ourEdge: cornerEdge,
                  theirEdge: cornerEdge,
                  region,
                };
              }
            }
          }
        }
      }
    }

    return { verticalSnapEdge, horizontalSnapEdge };

  }

  applyResize({ region, rect: {top, left, bottom, right} }) {

    const { verticalSnapEdge, horizontalSnapEdge } = this.getSnappableEdges({ top, left, bottom, right }, region.id);

    if (horizontalSnapEdge) {
      if (!edgeIsNear(region.unsnappedRect.left, horizontalSnapEdge.region[horizontalSnapEdge.theirEdge])) {
        region.left = region.unsnappedRect.left;
      } else if (horizontalSnapEdge.ourEdge === 'left') {
        region.left = horizontalSnapEdge.region[horizontalSnapEdge.theirEdge] + REGION_SNAP_GUTTER / 2;
      } else {
        region.left = left;
      }

      if (!edgeIsNear(region.unsnappedRect.right, horizontalSnapEdge.region[horizontalSnapEdge.theirEdge] )) {
        region.right = region.unsnappedRect.right;
      } else if (horizontalSnapEdge.ourEdge === 'right') {
        region.right = horizontalSnapEdge.region[horizontalSnapEdge.theirEdge] - REGION_SNAP_GUTTER / 2;
      } else {
        region.right = right;
      }
    } else {
      if (this.moving.right) region.right = right;
      if (this.moving.left) region.left = left;
    }


    if (verticalSnapEdge) {
      if (!edgeIsNear(region.unsnappedRect.top, verticalSnapEdge.region[verticalSnapEdge.theirEdge] )) {
        region.top = region.unsnappedRect.top;
      } else if (verticalSnapEdge.ourEdge === 'top') {
        region.top = verticalSnapEdge.region[verticalSnapEdge.theirEdge] + REGION_SNAP_GUTTER / 2;
      } else {
        region.top = top;
      }
      if (!edgeIsNear(region.unsnappedRect.bottom, verticalSnapEdge.region[verticalSnapEdge.theirEdge] )) {
        region.bottom = region.unsnappedRect.bottom;
      } else if (verticalSnapEdge.ourEdge === 'bottom') {
        region.bottom = verticalSnapEdge.region[verticalSnapEdge.theirEdge] - REGION_SNAP_GUTTER / 2;
      } else {
        region.bottom = bottom;
      }

    } else {
      if (this.moving.top) region.top = top;
      if (this.moving.bottom) region.bottom = bottom;
    }

  }

  prepareSelectedRegionsForMove() {
    if (!regionUI.selectedRegionIds || !regionUI.selectedRegionIds.size ) return;

    const regions = [];
    for (const regionId of regionUI.selectedRegionIds) {
      const region = canvas.store.getRegion(regionId);
      regions.push(region);
      for (const region of regions) {
        this.setPrevRegionPosition(region);
      }
    }

    this.addDraggableRegions(regions);
  }

  setPrevRegionPosition(region) {
    const { top, left, right, bottom } = region;
    this.prevRegionPositions[region.id] = { top, left, right, bottom };
  }

  prepareSelectedNodesForMove() {
    this.draggingNodes = this.canvas.getSelectedNodes();

    if (this.draggingNodes.length) {
      const startingNode = this.draggingNodes[0];
      const _targetIds = nui.computeAllSelectedMovingParts(this.canvas.store, startingNode, this.canvas);
      this.targetNodeIds = _targetIds.targetNodeIds;
      this.targetLinkIds = _targetIds.targetLinkIds;
      this.prevNodePositions = this.canvas.store.getNodePositions(this.targetNodeIds);
      this.prevLinkAngles = this.canvas.store.getLinkAngles(this.targetLinkIds);
    }
  }

  applyMoveToRegions({dx,dy, excludeRegions}) {
    if (regionUI.draggingRegions && regionUI.draggingRegions.length) {
      for (const region of this.draggingRegions.filter(r => !excludeRegions || !excludeRegions.includes(r.id))) {
        this.applyMove({ dx, dy, draggingRegion: region, dontSnap: true });
        const regionTag = this.getRegionTag(region.id);
        if (regionTag) {
          regionTag.refreshPosition();
        }
      }
    }
  }

  applyMoveToNodes({dx,dy}) {
    if (!this.draggingNodes|| !this.draggingNodes.length) return;

    const appliedMoveLookup = {};
    for (const node of this.draggingNodes) {
      node.applyMove({ dx, dy, appliedMoveLookup, canvas: this.canvas });
    }
    for (const node of this.draggingNodes) {
      const nodeTag = this.canvas.nodeTags[node.id];
      nodeTag.updatePosition();
      nodeTag.rediscoverLinkPositions();
    }
  }

  applyRegionPositionOperations({ operationMessages }) {
    if (!this.draggingRegions || !this.draggingRegions.length) return;

    for (const region of this.draggingRegions) {

      const message = this.getRegionPositionOperationMessage(region);
      operationMessages.push(message);
    }
  }
  getRegionPositionOperationMessage(region) {
    return this.canvas.operation.messages.updateRegion({
      regionUUID: region.id,
      prevValues: this.prevRegionPositions[region.id],
      updates: {
        top: region.top,
        left: region.left,
        right: region.right,
        bottom: region.bottom,
      },
    });
  }
  applyNodePositionOperations({ operationMessages }) {
    if (!this.draggingNodes || !this.draggingNodes.length) return;
    const nodePositions = canvas.store.getNodePositions(this.targetNodeIds);
    operationMessages.push(
      this.canvas.operation.messages.updateNodePositions({
        prevNodePositions: this.prevNodePositions,
        nodePositions: nodePositions,
        prevLinkAngles: this.prevLinkAngles,
        linkAngles: this.canvas.store.getLinkAngles(this.targetLinkIds),
        selectedNodes: this.draggingNodes.map(n => n.id),
      })
    );

  }


    /* list of regions that'll come along for the ride when dragging this region */
  addDraggableRegions(regions) {
    for (const region of regions) {
      if (regionUI.draggingRegions.find(r => r.id == region.id)) continue;
      regionUI.draggingRegions.push(region);
    }
  }


  applyMove({ draggingRegion, dx, dy, dontSnap }) {
    if (dontSnap) {
      draggingRegion.left += dx;
      draggingRegion.top += dy;
      draggingRegion.bottom += dy;
      draggingRegion.right += dx;
      return;
    }

    let leftissnapped = false;
    let rightissnapped = false;
    let topissnapped = false;
    let bottomissnapped = false;
    let leftsnapregion;
    let leftsnapedge;
    let rightsnapregion;
    let rightsnapedge;
    let bottomsnapregion;
    let bottomsnapedge;

    let topsnapregion;
    let topsnapedge;

    const width = draggingRegion.right - draggingRegion.left;
    const height = draggingRegion.bottom - draggingRegion.top;

    const snapRange = REGION_SNAP_RANGE;

    const draggingRect = expandRect(draggingRegion, REGION_SNAP_GUTTER / 2);

    const nonSelfRegions = canvas.store.getRegions()
      .filter( region => region.id !== draggingRegion.id )
      .map(r => expandRect(r, REGION_SNAP_GUTTER / 2));

    const horizontalSnappableRegions = nonSelfRegions
      .filter( region => {
        return (
              Math.abs(region.right - draggingRect.left) < width
              || Math.abs(region.left - draggingRect.right) < width
            )
          && (
            verticalIntersection(draggingRect, region) || verticalIntersection(region, draggingRect)
          );
      });

    const verticalSnappableRegions = nonSelfRegions
      .filter( region => {
        return (
              Math.abs(region.bottom - draggingRect.top) < height
              || Math.abs(region.top - draggingRect.bottom) < height
            )
          && (
            horizontalIntersection(draggingRect, region) || horizontalIntersection(region, draggingRect)
          );
      });

    for (const region of horizontalSnappableRegions) {
      if (edgeIsNear(draggingRect.left, region.right)) {
        leftissnapped = true;
        leftsnapregion = region;
        leftsnapedge = 'right';
        draggingRegion.horizontalMove += dx;
      } else if (edgeIsNear(draggingRect.right, region.left)) {
        rightissnapped = true;
        rightsnapregion = region;
        rightsnapedge = 'left';
        draggingRegion.horizontalMove += dx;
      }
    }

    for (const region of verticalSnappableRegions) {
      if (edgeIsNear(draggingRect.top, region.bottom)) {
        topissnapped = true;
        topsnapregion = region;
        topsnapedge = 'bottom';
        draggingRegion.verticalMove += dy;
      } else if (edgeIsNear(draggingRect.bottom, region.top)) {
        bottomissnapped = true;
        bottomsnapregion = region;
        bottomsnapedge = 'top';
        draggingRegion.verticalMove += dy;
      }
    }

    //corners

    if (leftissnapped) {
      if (edgeIsNear(leftsnapregion.top, draggingRect.top)) {
        topissnapped = true;
        topsnapregion = leftsnapregion;
        topsnapedge = 'top';
        draggingRegion.verticalMove += dy;
      } else if (edgeIsNear(leftsnapregion.bottom, draggingRect.bottom)) {
        bottomissnapped = true;
        bottomsnapregion = leftsnapregion;
        bottomsnapedge = 'bottom';
        draggingRegion.verticalMove += dy;
      }
    }
    if (rightissnapped) {
      if (edgeIsNear(rightsnapregion.top, draggingRect.top)) {
        topissnapped = true;
        topsnapregion = rightsnapregion;
        topsnapedge = 'top';
        draggingRegion.verticalMove += dy;
      } else if (edgeIsNear(rightsnapregion.bottom, draggingRect.bottom)) {
        bottomissnapped = true;
        bottomsnapregion = rightsnapregion;
        bottomsnapedge = 'bottom';
        draggingRegion.verticalMove += dy;
      }
    }
    if (topissnapped) {
      if (edgeIsNear(topsnapregion.left, draggingRect.left)) {
        leftissnapped = true;
        leftsnapregion = topsnapregion;
        leftsnapedge = 'left';
        draggingRegion.horizontalMove += dx;
      } else if (edgeIsNear(topsnapregion.right, draggingRect.right)) {
        rightissnapped = true;
        rightsnapregion = topsnapregion;
        rightsnapedge = 'right';
        draggingRegion.horizontalMove += dx;
      }

    }
    if (bottomissnapped) {
      if (edgeIsNear(bottomsnapregion.left, draggingRect.left)) {
        leftissnapped = true;
        leftsnapregion = bottomsnapregion;
        leftsnapedge = 'left';
        draggingRegion.horizontalMove += dx;
      } else if (edgeIsNear(bottomsnapregion.right, draggingRect.right)) {
        rightissnapped = true;
        rightsnapregion = bottomsnapregion;
        rightsnapedge = 'right';
        draggingRegion.horizontalMove += dx;
      }

    }

    //actually move stuff horizontally

    if (Math.abs(draggingRegion.horizontalMove) > snapRange) {
      draggingRegion.left = draggingRegion.left + draggingRegion.horizontalMove;
      draggingRegion.right = draggingRegion.right + draggingRegion.horizontalMove;
      draggingRegion.horizontalMove = 0;
    } else if (leftissnapped) {
      draggingRegion.left = leftsnapregion[leftsnapedge] + REGION_SNAP_GUTTER / 2;
      draggingRegion.right = leftsnapregion[leftsnapedge] + width + REGION_SNAP_GUTTER / 2;
    } else if (rightissnapped) {
      draggingRegion.right = rightsnapregion[rightsnapedge] - REGION_SNAP_GUTTER / 2;
      draggingRegion.left = rightsnapregion[rightsnapedge] - width - REGION_SNAP_GUTTER / 2;
    } else {
      draggingRegion.left += dx;
      draggingRegion.right += dx;
    }

    //actually move stuff vertically

    if (Math.abs(draggingRegion.verticalMove) > snapRange) {
      draggingRegion.top = draggingRegion.top + draggingRegion.verticalMove;
      draggingRegion.bottom = draggingRegion.bottom + draggingRegion.verticalMove;
      draggingRegion.verticalMove = 0;
    } else if (topissnapped) {
      draggingRegion.top = topsnapregion[topsnapedge] + REGION_SNAP_GUTTER / 2;
      draggingRegion.bottom = topsnapregion[topsnapedge] + height + REGION_SNAP_GUTTER / 2;
    } else if (bottomissnapped) {
      draggingRegion.bottom = bottomsnapregion[bottomsnapedge] - REGION_SNAP_GUTTER / 2;
      draggingRegion.top = bottomsnapregion[bottomsnapedge] - height - REGION_SNAP_GUTTER / 2;
    } else {
      draggingRegion.top += dy;
      draggingRegion.bottom += dy;
    }
  }

  nudgeRegion({ regionUUID, dx = 0, dy = 0 }) {
    const draggingRegion = store.getRegion(regionUUID);
    const regionTag = this.canvas.regionTags[regionUUID];

    if (draggingRegion) {
      regionUI.applyMove({
        draggingRegion,
        dx, dy,
        dontSnap: true,
      });

      if (regionTag) {
        regionTag.refreshPosition();
      }
    }
  }
}

const regionUI = new RegionUI;
window.regionUI = regionUI;
export { RegionUI, regionUI };



// WEBPACK FOOTER //
// ./starling/src/lib/region-ui.js